import type { IntlShape } from "@formatjs/intl";
import { Button, Col, Empty, Flex, Row, Space, Typography, theme } from "antd";
import { useMemo } from "react";
import {
  FilePreviewModal,
  useFilePreview,
} from "../../../../common/components/FilePreviewModal";
import { buildDownloadUrl } from "../FilesInfo";
import { ReportPictureTile } from "./ReportPictureTile";
import type { ReportPicture } from "./reportPictures";

// Matches the PDF, which prints three pictures per row.
const PICTURE_COLUMN_SPAN = 8;

type ReportPicturePickerProps = {
  pictures: ReportPicture[];
  selectedFileIds: string[];
  onChange: (selectedFileIds: string[]) => void;
  emptyText: string;
  intl: IntlShape;
};

export function ReportPicturePicker({
  pictures,
  selectedFileIds,
  onChange,
  emptyText,
  intl,
}: ReportPicturePickerProps) {
  const previewableFiles = useMemo(
    () =>
      pictures.map((picture) => ({
        id: picture.fileId,
        name: picture.name,
        content_type: picture.contentType,
      })),
    [pictures]
  );
  const filePreview = useFilePreview(previewableFiles);
  const { token } = theme.useToken();

  const includablePictureIds = pictures
    .filter((picture) => picture.canBeIncluded)
    .map((picture) => picture.fileId);
  const selectedIds = new Set(selectedFileIds);

  // kept in the order the pictures are shown, which is the order they print in
  const toggle = (fileId: string) =>
    onChange(
      includablePictureIds.filter((id) =>
        id === fileId ? !selectedIds.has(id) : selectedIds.has(id)
      )
    );

  if (pictures.length === 0) {
    return <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyText} />;
  }

  return (
    <Flex vertical gap="middle">
      <Flex align="center" justify="space-between" wrap="wrap" gap="small">
        <Typography.Text type="secondary">
          {intl.formatMessage(
            { id: "wo_report_pictures_selected" },
            { selected: selectedFileIds.length, total: pictures.length }
          )}
        </Typography.Text>
        <Space>
          <Button
            size="small"
            disabled={selectedFileIds.length === includablePictureIds.length}
            onClick={() => onChange(includablePictureIds)}
          >
            {intl.formatMessage({ id: "wo_report_select_all" })}
          </Button>
          <Button
            size="small"
            disabled={selectedFileIds.length === 0}
            onClick={() => onChange([])}
          >
            {intl.formatMessage({ id: "wo_report_clear_selection" })}
          </Button>
        </Space>
      </Flex>
      <Row gutter={[token.margin, token.margin]}>
        {pictures.map((picture) => (
          <Col key={picture.fileId} span={PICTURE_COLUMN_SPAN}>
            <ReportPictureTile
              picture={picture}
              selected={selectedIds.has(picture.fileId)}
              onToggle={() => toggle(picture.fileId)}
              onPreview={() => filePreview.openPreview({ id: picture.fileId })}
              intl={intl}
            />
          </Col>
        ))}
      </Row>
      <FilePreviewModal
        preview={filePreview}
        getDownloadUrl={buildDownloadUrl}
        intl={intl}
      />
    </Flex>
  );
}
